import React from 'react';
import { useTranslation } from 'react-i18next';
import ArrowRightIcon from '@/assets/icons/home-v2/arrow-right.svg?react';
import ArrowRightDisabledIcon from '@/assets/icons/home-v2/arrow-right-disabled.svg?react';
import './ProductRowCard.css';

export interface ProductRowCardProps {
  /** Product name */
  title: string;
  /** Short product description */
  description: string;
  /** 36×36 icon badge exported from Figma */
  icon: React.ReactNode;
  /** Called when the card is activated (click or keyboard) */
  onAccess?: () => void;
  /** When true the card is inert (used by the "EM BREVE" products) */
  disabled?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * ProductRowCard
 *
 * Single-row product card of the new home: icon badge, title, description and a
 * trailing arrow. The whole card is the click target. Figma nodes `1601:13751`,
 * `1601:13931`, `1601:14105` (disabled variant).
 *
 * @example
 * ```tsx
 * <ProductRowCard
 *   title={t('Individual consultation')}
 *   description={t('Perform queries in PDT modules')}
 *   icon={<ManageSearchBadge width={36} height={36} aria-hidden="true" />}
 *   onAccess={() => navigateTo(PDT_LINKS.CONSULTA_INDIVIDUAL)}
 * />
 * ```
 *
 * @since 1.0.0
 * @category Molecules
 */
export const ProductRowCard: React.FC<ProductRowCardProps> = ({
  title,
  description,
  icon,
  onAccess,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();

  const modifier = disabled ? 'product-row-card--disabled' : '';

  return (
    <button
      type="button"
      className={`product-row-card ${modifier} ${className}`.trim()}
      onClick={disabled ? undefined : onAccess}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-label={
        disabled
          ? t('Available soon')
          : t('Access {{product}}', { product: title })
      }
    >
      <span className="product-row-card__content">
        <span className="product-row-card__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="product-row-card__text">
          <span className="product-row-card__title">{title}</span>
          <span className="product-row-card__description">{description}</span>
        </span>
      </span>
      <span className="product-row-card__arrow" aria-hidden="true">
        {disabled ? (
          <ArrowRightDisabledIcon width={20} height={20} />
        ) : (
          <ArrowRightIcon width={20} height={20} />
        )}
      </span>
    </button>
  );
};

export default ProductRowCard;
